import React, { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Common } from "@src/styles/Common";
import styled, { css } from "styled-components";

import MgInput from "@src/components/input/MgInput";
import CheckIcon from "@src/assets/Signup/CheckIcon";
import Remove from "@src/assets/Signup/remove";
import { CounselingCategory, OfficeInfo, uploadExpertProfileImage } from "@src/api/expert/expertInfo";
import { COUNCELING_CATEGORIES_ORDER } from "@src/pages/signupExpert";
import UserOfficeInfoCard from "./UserOfficeInfoCard";
import { transferToNative, getDeviceType } from "@src/lib/WebAppBridge";

const CATEGORY_DISPLAY_TEXT: Record<CounselingCategory, string> = {
    중개: "공인중개사로 등록할래요",
    매물컨설팅: "매물 컨설팅 전문가로 등록할래요",
    매수신청대리인: "매수신청대리인으로 등록할래요",
    임장동행: "임장동행 전문가로 등록 할래요",
};

type Props = {
    isApproved: boolean;
    availableCategories?: CounselingCategory[];
    isEditing: boolean;
    counselingCategories: CounselingCategory[];
    career: { [key: string]: number };
    officeInfo?: OfficeInfo;
    auctionAgentCertFileUrl?: string;
    onCategoriesChange: (
        categories: CounselingCategory[],
        career: { [key: string]: number },
    ) => void;
    onCertFileChange: (url: string) => void;
};

const ExpertSelectInfo: React.FC<Props> = ({
    isApproved,
    availableCategories = COUNCELING_CATEGORIES_ORDER,
    isEditing,
    counselingCategories,
    career,
    officeInfo,
    auctionAgentCertFileUrl,
    onCategoriesChange,
    onCertFileChange,
}) => {
    const navigate = useNavigate();
    const inputRef = useRef<HTMLInputElement | null>(null);

    const toggleCategory = (category: CounselingCategory) => {
        const selected = counselingCategories.includes(category);
        let newCategories = [...counselingCategories];
        let newCareer = { ...career };

        if (selected) {
            newCategories = newCategories.filter((c) => c !== category);
            delete newCareer[category];
        } else {
            newCategories.push(category);
            newCareer[category] = 0;
        }
        onCategoriesChange(newCategories, newCareer);
    };

    const handleCertUpload: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const url = await uploadExpertProfileImage(file);
        onCertFileChange(url);
    };

    const handleClickUpload = () => {
        if (getDeviceType() === "android") {
            transferToNative("", "onClickCertificate");
            return;
        }
        inputRef.current?.click();
    };

    const handleOfficeSearch = () => {
        navigate("/expert/officeSearch", { state: { from: location.pathname } });
    };

    const getOfficeSummary = () => {
        if (!officeInfo?.name) return "";
        return `${officeInfo.name}${officeInfo.address ? ` (${officeInfo.address})` : ""}`;
    };

    const delegationOn = counselingCategories.includes("매수신청대리인");
    const brokerageOn = counselingCategories.includes("중개");

    return (
        <Container>
            <Tabs role="tablist">
                {availableCategories.map((category) => {
                    const isDelegation = category === "매수신청대리인";
                    const selected = counselingCategories.includes(category);
                    const delegationPending = isDelegation && selected && !isEditing && !isApproved;

                    return isEditing || selected ? (
                        <Tab
                            key={category}
                            type="button"
                            role="tab"
                            aria-selected={selected}
                            data-active={selected ? "true" : "false"}
                            $disabled={delegationPending}
                            onClick={isEditing ? () => toggleCategory(category) : undefined}
                        >
                            {isDelegation ? "경매대행" : category}
                        </Tab>
                    ) : null;
                })}
            </Tabs>

            <AlwaysList>
                {COUNCELING_CATEGORIES_ORDER.map(
                    (category) =>
                        isEditing && (
                            <Row key={category}>
                                <ListButton
                                    type="button"
                                    onClick={() => toggleCategory(category)}
                                    $editing={isEditing}
                                >
                                    <CheckIcon isChecked={counselingCategories.includes(category)} />
                                    <ListText>{CATEGORY_DISPLAY_TEXT[category]}</ListText>
                                </ListButton>
                            </Row>
                        ),
                )}

                {isEditing && (delegationOn || brokerageOn) && (
                    officeInfo?.name ? (
                        <OfficeInputWrapper>
                            <InputHeader>
                                <span className="star">*</span>
                                <span className="title">중개사무소 정보</span>
                            </InputHeader>
                            <UserOfficeInfoCard officeInfo={officeInfo} />
                            <EditButton type="button" onClick={handleOfficeSearch}>
                                다른 중개사무소 선택하기
                            </EditButton>
                        </OfficeInputWrapper>
                    ) : (
                        <OfficeInputWrapper onClick={handleOfficeSearch}>
                            <InputHeader>
                                <span className="star">*</span>
                                <span className="title">중개사무소 조회</span>
                            </InputHeader>
                            <MgInput
                                placeholder="조회하여 입력이 가능합니다."
                                value={getOfficeSummary()}
                                readOnly
                                noBorder
                                bgColor="#FBF8F8"
                            />
                        </OfficeInputWrapper>
                    )
                )}

                {isEditing && delegationOn && (
                    <DelegationWrap>
                        <HelperText>매수신청대리인만이 경매대행 업무가 가능해요.</HelperText>
                        <Line />
                        <Upload>
                            <SectionTitle>매수신청대리인 등록증</SectionTitle>
                            <Button type="button" onClick={handleClickUpload}>
                                사진 업로드
                            </Button>
                            <HiddenFileInput ref={inputRef} type="file" accept="image/*" onChange={handleCertUpload} />
                        </Upload>
                        {auctionAgentCertFileUrl && (
                            <PreviewContainer>
                                <Item>
                                    <img src={auctionAgentCertFileUrl} alt="업로드 미리보기" />
                                    <RemoveBtn type="button" onClick={() => onCertFileChange("")}>
                                        <Remove />
                                    </RemoveBtn>
                                </Item>
                            </PreviewContainer>
                        )}
                    </DelegationWrap>
                )}
            </AlwaysList>
        </Container>
    );
};

export default ExpertSelectInfo;

const Container = styled.div``;
const AlwaysList = styled.div`display: flex; flex-direction: column; gap: 24px;`;
const Row = styled.div`display: flex; flex-direction: column;`;

const ListButton = styled.button<{ $editing?: boolean }>`
    display: ${({ $editing }) => ($editing ? "flex" : "none")};
    align-items: center;
    gap: 8px;
    cursor: pointer;
    text-align: left;
    background: transparent;
    &:focus-visible { outline: 2px solid #2d73ff; border-radius: 8px; }
`;

const ListText = styled.span`
    font-size: 16px; font-weight: 700; line-height: 140%; color: #140c2c;
`;

const InputHeader = styled.div`
    margin-top: 24px;
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 8px;
    .star { color: ${Common.colors.notiColor}; }
    .title { color: ${Common.colors.normalTextColor}; font-size: 16px; font-weight: 400; line-height: 140%; }
`;

const HelperText = styled.p`margin: 0; font-size: 14px; color: #a3a3a3; line-height: 140%;`;
const DelegationWrap = styled.div`margin: 0 0 0 34px;`;
const Upload = styled.div`display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap;`;
const Line = styled.div`margin: 8px 0; width: 100%; height: 1px; background-color: #f2f3f6;`;
const SectionTitle = styled.h3`font-size: 14px; font-weight: 400; color: #0c002c; line-height: 140%; margin: 0;`;

const OfficeInputWrapper = styled.div`
    width: 100%;
    cursor: pointer;
    & input, & textarea { text-overflow: ellipsis; white-space: nowrap; overflow: hidden; }
`;

const EditButton = styled.button`
    margin-top: 12px; padding: 8px 16px; background: transparent; color: #2d73ff;
    border: 1px solid #2d73ff; border-radius: 8px; font-size: 14px; font-weight: 500;
    cursor: pointer; width: 100%; transition: all 0.2s;
    &:hover { background: #f5f8ff; }
`;

const Button = styled.button`
    padding: 3px 10px; background: #2d73ff; color: #fff; border: none;
    cursor: pointer; border-radius: 8px; font-weight: 400; font-size: 14px;
`;

const HiddenFileInput = styled.input`display: none;`;

const PreviewContainer = styled.div`
    display: grid; grid-template-columns: repeat(auto-fill, minmax(90px, 1fr)); gap: 12px; padding-top: 12px;
`;

const Item = styled.div`
    position: relative; border: 1px solid #505050; border-radius: 8px; aspect-ratio: 1 / 1;
    img { width: 100%; height: 100%; object-fit: cover; display: block; border-radius: 8px; }
`;

const RemoveBtn = styled.button`
    position: absolute; top: 0; right: 0; transform: translate(35%, -35%);
    width: 24px; height: 24px; display: flex; align-items: center; justify-content: center;
    padding: 0; border: 0; cursor: pointer; background: transparent;
    svg { width: 24px; height: 24px; display: block; }
`;

const Tabs = styled.div`
    display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 4px; margin-bottom: 10px;
`;

const Tab = styled.button<{ $disabled?: boolean }>`
    height: 44px; 
    border-radius: 1000px; 
    border: 0; font-size: 14px; font-weight: 400;
    letter-spacing: -0.2px; cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
    opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
    ${({ $disabled }) => css`
        &[data-active="true"] { background: ${$disabled ? "#C3C3C3" : "#2275ee"}; color: #fff; }
        &[data-active="false"] { background: ${$disabled ? "#C3C3C3" : "#fbf8f8"}; color: ${$disabled ? "#fff" : "#c9c9c9"}; }
    `}
    &:focus-visible { outline: 2px solid #2d73ff; }
    @media screen and (max-width: 350px) { font-size: 13px; }
`;